import React from "react";
import "./MediaPlayerControls.css";

interface MediaPlayerControlsProps {
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  onPlayPause: () => void;
  onSeek: (time: number) => void;
  muted: boolean;
  onMuteToggle: () => void;
}

function formatTime(seconds: number) {
  if (isNaN(seconds)) return "0:00";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export const MediaPlayerControls: React.FC<MediaPlayerControlsProps> = ({
  isPlaying,
  currentTime,
  duration,
  onPlayPause,
  onSeek,
  muted,
  onMuteToggle,
}) => {
  // Calculate progress as a percentage
  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  // Handlers
  const handleProgressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = Number(e.target.value);
    onSeek((value / 100) * duration);
  };

  return (
    <div className="absolute bottom-0 left-0 right-0 bg-transparent px-4 py-2 flex items-center gap-4 text-white select-none z-10 rounded-b-lg">
      {/* Play/Pause */}
      <button
        onClick={onPlayPause}
        className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-white/10 focus:outline-none"
        aria-label={isPlaying ? "Pause" : "Play"}
      >
        {isPlaying ? (
          <svg width="22" height="22" fill="currentColor" viewBox="0 0 22 22">
            <rect x="4" y="3" width="5" height="16" rx="1.5" />
            <rect x="13" y="3" width="5" height="16" rx="1.5" />
          </svg>
        ) : (
          <svg width="22" height="22" fill="currentColor" viewBox="0 0 22 22">
            <polygon points="5,3 19,11 5,19" />
          </svg>
        )}
      </button>
      {/* Progress Bar */}
      <div className="flex-1 flex items-center gap-2">
        <span className="text-xs w-12 text-right font-mono">
          {formatTime(currentTime)}
        </span>
        <input
          type="range"
          min={0}
          max={100}
          value={progress}
          onChange={handleProgressChange}
          className="media-player-progress flex-1 h-2 mx-2"
          style={{ "--progress": `${progress}%` } as React.CSSProperties}
        />
        <span className="text-xs w-12 font-mono">{formatTime(duration)}</span>
      </div>
      {/* Mute */}
      <button
        onClick={onMuteToggle}
        className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-white/10 focus:outline-none"
        aria-label={muted ? "Unmute" : "Mute"}
      >
        {muted ? (
          <svg width="22" height="22" fill="currentColor" viewBox="0 0 22 22">
            <path d="M4 9v4h4l5 5V4l-5 5H4z" />
            <line
              x1="17"
              y1="7"
              x2="21"
              y2="15"
              stroke="currentColor"
              strokeWidth="2"
            />
          </svg>
        ) : (
          <svg width="22" height="22" fill="currentColor" viewBox="0 0 22 22">
            <path d="M4 9v4h4l5 5V4l-5 5H4z" />
          </svg>
        )}
      </button>
    </div>
  );
};
